import { useSelector } from 'react-redux';
import useMediaQuery from '@mui/material/useMediaQuery';
import Drawer from '@mui/material/Drawer';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { Typography } from '@components/Typography';
import ContactDeleteModal from '@features/ContactDeleteModal';
import ContactsMergeModal from '@features/ContactsMergeModal';
import EditContactModal from '@features/EditContactModal';
import ContactInfoComponent from '@features/ContactInfo';
import InitConversation from '@features/InitConversation';
import Avatar from '@components/Avatar';
import { getInitialsParts, getFullName, getUserContacts } from '@helpers/userName';
import { UserIcon } from 'assets/images/icons';

const StyledUserIcon = styled(UserIcon)({
    width: '16px !important',
    height: '16px !important',
    '& path': {
        fill: 'rgba(0, 0, 0, 0.38)',
        fillOpacity: 1,
    },
});

const StyledDrawer = styled(Drawer)(() => ({
    width: '360px',
    zIndex: 2,
    '& .MuiDrawer-paper': {
        borderLeft: 'none',
        width: '360px',
        top: '85px',
        right: '24px',
        overflowY: 'unset',
        '@media (max-width: 1480px)': {
            width: '270px',
        },
        '@media (max-width: 1280px)': {
            width: '200px',
        },
        '@media (max-width: 1024px)': {
            width: '100%',
            top: '108px',
            right: 0,
        },
    },
}));

const Header = styled('div')({
    display: 'grid',
    padding: '5px 11px 19px 16px',
    gridGap: '0 16px',
    gridTemplateColumns: '32px 1fr 24px',
    alignItems: 'center',
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    '@media (min-width: 1025px) and (max-width: 1280px)': {
        padding: '0px 8px 13px 8px',
        height: 57,
        gridTemplateColumns: '32px 1fr',
        gridGap: '0 8px',
    },
    '@media (max-width: 1024px)': {
        padding: '12px 16px',
    },
});

const Content = styled('div')({
    padding: 16,
    height: 'calc(var(--100vh) - 153px)',
    overflowY: 'auto',
    '@media (min-width: 1025px) and (max-width: 1280px)': {
        padding: 8,
    },
    '@media (max-width: 1024px)': {
        height: 'calc(var(--100vh) - 165px)',
    },
});

const StyledCloseIcon = styled(CloseIcon)({
    fill: 'rgba(0, 0, 0, 0.6)',
    cursor: 'pointer',
    '@media (min-width: 1025px) and (max-width: 1280px)': {
        marginLeft: 'auto',
    },
});

const ContactInfo = ({
    open,
    onClose,
    setActiveConversation = null,
    onDeleteCallback,
    onMergeCallback,
    onUpdateCallback,
    onInitConversationCallback,
}) => {
    const { contact, contactMergeModal, contactDeleteModal, contactEditModal, contactInitConversationModal } =
        useSelector(state => state.contacts);
    const isLaptopScreen = useMediaQuery('@media (min-width: 1025px) and (max-width: 1280px)');

    return (
        <>
            <StyledDrawer variant="persistent" anchor="right" open={open}>
                <Header>
                    <Avatar
                        size="medium"
                        fullName={getFullName(contact?.firstName, contact?.lastName)}
                        label={getInitialsParts(contact?.firstName, contact?.lastName)}
                        icon={<StyledUserIcon />}
                    />
                    {!isLaptopScreen && (
                        <Typography
                            variant="h6"
                            sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                            {getUserContacts(
                                {
                                    firstName: contact?.firstName,
                                    lastName: contact?.lastName,
                                    phoneNumber: contact?.phoneNumber,
                                    email: contact?.email,
                                    externalId: contact?.externalId,
                                },
                                '-',
                            )}
                        </Typography>
                    )}
                    <StyledCloseIcon onClick={onClose} />
                </Header>
                <Content>
                    <ContactInfoComponent contact={contact} resizable setActiveConversation={setActiveConversation} />
                </Content>
            </StyledDrawer>
            {contactDeleteModal ? (
                <ContactDeleteModal onDeleteCallback={onDeleteCallback} open={contactDeleteModal} />
            ) : null}
            {contactMergeModal ? (
                <ContactsMergeModal onMergeCallback={onMergeCallback} open={contactMergeModal} />
            ) : null}
            {contactEditModal.open ? (
                <EditContactModal onUpdateCallback={onUpdateCallback} modal={contactEditModal} />
            ) : null}
            {contactInitConversationModal.open ? (
                <InitConversation onInitConversationCallback={onInitConversationCallback} />
            ) : null}
        </>
    );
};

export default ContactInfo;
